/**
 * Date and time formatting utilities for workplace settings
 * Handles different date formats and time formats based on user preferences
 */

export type DateFormat = 'DD/MM/YYYY' | 'MM/DD/YYYY' | 'YYYY-MM-DD';
export type TimeFormat = '12h' | '24h';

/**
 * Format a date according to the specified format
 */
export const formatDateByFormat = (date: Date | string, format: DateFormat): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  if (isNaN(dateObj.getTime())) {
    return 'Invalid Date';
  }

  const day = dateObj.getDate().toString().padStart(2, '0');
  const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
  const year = dateObj.getFullYear().toString();

  switch (format) {
    case 'DD/MM/YYYY':
      return `${day}/${month}/${year}`;
    case 'MM/DD/YYYY':
      return `${month}/${day}/${year}`;
    case 'YYYY-MM-DD':
      return `${year}-${month}-${day}`;
    default:
      return `${day}/${month}/${year}`;
  }
};

/**
 * Format a time according to the specified format (12h or 24h)
 */
export const formatTimeByFormat = (time: Date | string, format: TimeFormat): string => {
  const timeObj = typeof time === 'string' ? new Date(time) : time;
  
  if (isNaN(timeObj.getTime())) {
    return 'Invalid Time';
  }

  if (format === '24h') {
    const hours = timeObj.getHours().toString().padStart(2, '0');
    const minutes = timeObj.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  } else {
    // 12h format
    let hours = timeObj.getHours();
    const minutes = timeObj.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    
    hours = hours % 12;
    hours = hours ? hours : 12; // 0 should be 12
    
    return `${hours}:${minutes} ${ampm}`;
  }
};

/**
 * Format a datetime string according to both date and time preferences
 */
export const formatDateTimeByFormat = (
  datetime: Date | string,
  dateFormat: DateFormat,
  timeFormat: TimeFormat
): string => {
  const dateObj = typeof datetime === 'string' ? new Date(datetime) : datetime;
  
  if (isNaN(dateObj.getTime())) {
    return 'Invalid DateTime';
  }

  const formattedDate = formatDateByFormat(dateObj, dateFormat);
  const formattedTime = formatTimeByFormat(dateObj, timeFormat);
  
  return `${formattedDate} ${formattedTime}`;
};

/**
 * Parse a date string in the specified format back to a Date object
 */
export const parseDateByFormat = (dateString: string, format: DateFormat): Date | null => {
  if (!dateString.trim()) return null;

  let day: string, month: string, year: string;

  switch (format) {
    case 'DD/MM/YYYY':
      [day, month, year] = dateString.split('/');
      break;
    case 'MM/DD/YYYY':
      [month, day, year] = dateString.split('/');
      break;
    case 'YYYY-MM-DD':
      [year, month, day] = dateString.split('-');
      break;
    default:
      return null;
  }

  if (!day || !month || !year) return null;

  const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  
  // Validate the date
  if (
    date.getFullYear() !== parseInt(year) ||
    date.getMonth() !== parseInt(month) - 1 ||
    date.getDate() !== parseInt(day)
  ) {
    return null;
  }

  return date;
};

/**
 * Get the date format pattern for input placeholders
 */
export const getDateFormatPattern = (format: DateFormat): string => {
  switch (format) {
    case 'DD/MM/YYYY':
      return 'dd/mm/yyyy';
    case 'MM/DD/YYYY':
      return 'mm/dd/yyyy';
    case 'YYYY-MM-DD':
      return 'yyyy-mm-dd';
    default:
      return 'dd/mm/yyyy';
  }
};

/**
 * Get the date format separator
 */
export const getDateFormatSeparator = (format: DateFormat): string => {
  return format === 'YYYY-MM-DD' ? '-' : '/';
};

/**
 * Validate a hex color string
 */
export const validateHexColor = (color: string): boolean => {
  const hexColorRegex = /^#[0-9A-Fa-f]{6}$/;
  return hexColorRegex.test(color);
};

/**
 * Convert a hex color to RGB values
 */
export const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
  if (!validateHexColor(hex)) return null;

  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
};

/**
 * Convert RGB values to hex color
 */
export const rgbToHex = (r: number, g: number, b: number): string => {
  const toHex = (n: number) => {
    const hex = Math.round(Math.max(0, Math.min(255, n))).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };
  
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

/**
 * Get a contrasting text color (black or white) for a given background color
 */
export const getContrastingTextColor = (backgroundColor: string): string => {
  const rgb = hexToRgb(backgroundColor);
  if (!rgb) return '#000000';

  // Calculate relative luminance
  const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
  
  // Return black for light backgrounds, white for dark backgrounds
  return luminance > 0.5 ? '#000000' : '#ffffff';
};

/**
 * Available date format options for dropdowns
 */
export const DATE_FORMAT_OPTIONS = [
  { value: 'DD/MM/YYYY' as DateFormat, label: 'DD/MM/YYYY', example: '31/12/2024' },
  { value: 'MM/DD/YYYY' as DateFormat, label: 'MM/DD/YYYY', example: '12/31/2024' },
  { value: 'YYYY-MM-DD' as DateFormat, label: 'YYYY-MM-DD', example: '2024-12-31' },
];

/**
 * Available time format options for toggles
 */
export const TIME_FORMAT_OPTIONS = [
  { value: '12h' as TimeFormat, label: '12-hour (AM/PM)', example: '2:30 PM' },
  { value: '24h' as TimeFormat, label: '24-hour', example: '14:30' },
];

/**
 * Format currency based on currency code
 * @param amount - The amount to format
 * @param currencyCode - Optional currency code (defaults to USD if not provided)
 */
export const formatCurrency = (amount: number, currencyCode?: string): string => {
  const currency = currencyCode || 'USD';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  } catch (error) {
    // Fallback if currency code is invalid
    return `${currency} ${amount.toFixed(2)}`;
  }
};
